
import { Vue, Prop, Component, Watch } from "vue-property-decorator";
import UiFileInputComponent from "@controls/ui-file-input.vue";
import CropImageComponent from "crop-image/src/crop-image.vue";
import ImagePreviewModel from "crop-image/src/ImagePreviewModel";
import { parseGIF, decompressFrames } from "gifuct-js";
import { GIFEncoder, quantize, applyPalette } from "gifenc";

//import ImagePreview from "@models/imagePreview";

@Component({
  name: "ui-image-croper",
  components: {
    "ui-file-input": UiFileInputComponent,
    "crop-image": CropImageComponent,
  },
})
export default class UiImageCroperComponent extends Vue {
  cropImageUrl = "";
  file: File;
  cropFile: File;
  pendingCropParams: ImagePreviewModel = null;
  gifStillImageUrl = "";
  cropGifStillImageUrl = "";
  isGifPlaying = true;
  isCropGifPlaying = true;
  errorMessage: string = "";
  hasError: boolean = false;
  cropMaxHeight = 300;
  cropMaxWidth = 450;
   
  @Prop({ default: () =>  new ImagePreviewModel() }) value: ImagePreviewModel;
  innervalue: ImagePreviewModel = this.value;
  @Watch("innervalue")
  onInnerValueChanged() {
    this.$emit('input', this.innervalue);
  }
  @Watch("value")
  onValueChanged() {
    this.innervalue = this.value;
    this.syncExistingImageMeta();
  }
  @Prop({ default: true })
  showError!: boolean;
  @Prop({ default: true })
  required!: boolean;
  @Prop({default:1})
  aspectRatio:number;
  @Prop({ default: "image/png, image/jpeg, image/webp, .webp" })
  accept!: string;
  @Prop({ default: false })
  preserveGif!: boolean;
  get previewSource(): string {
    if (!this.isGifPlaying && this.gifStillImageUrl) {
      return this.gifStillImageUrl;
    }
    return (this.innervalue && this.innervalue.ImageUrl) || "/assets/img/no_image.svg";
  }
  get previewBlockStyle(): Object {
    return {
      height: "182px",
      width: `${182 * (this.aspectRatio || 1)}px`,
    };
  }
  get previewImageStyle(): Object {
    if (!this.innervalue || !this.innervalue.ImageUrl || !this.innervalue.Width || !this.innervalue.Height) {
      return {
        height: "182px",
        width: "100%",
      };
    }

    const aspect = 182 / this.innervalue.Height;
    return {
      left: `${-(this.innervalue.Left || 0) * aspect}px`,
      top: `${-(this.innervalue.Top || 0) * aspect}px`,
      height: `${this.getPreviewNaturalHeight() * aspect}px`,
    };
  }
  get cropModalImageUrl(): string {
    if (this.isGifImage(this.cropImageUrl) && !this.isCropGifPlaying && this.cropGifStillImageUrl) {
      return this.cropGifStillImageUrl;
    }
    return this.cropImageUrl;
  }
  get cropPrevParams(): Object {
    const cropParams = this.pendingCropParams;
    return {
      left: cropParams ? cropParams.Left || 0 : 0,
      top: cropParams ? cropParams.Top || 0 : 0,
      width: cropParams ? cropParams.Width || 0 : 0,
      height: cropParams ? cropParams.Height || 0 : 0,
    };
  }
  mounted() {
    this.syncExistingImageMeta();
    this.updateCropLimits();
    window.addEventListener("resize", this.updateCropLimits);
  }
  beforeDestroy() {
    window.removeEventListener("resize", this.updateCropLimits);
  }
  startChooseFile() {
    (this.$refs.fileInput as UiFileInputComponent).onClick();
  }
  removeImage(){
    this.clearFile;
    const newData = new ImagePreviewModel();
    newData.ImageUrl = '';
    newData.Width = 0;
    newData.Height = 0;
    newData.Top = 0;
    newData.Left = 0;
    Object.assign(this.innervalue, newData);
    this.gifStillImageUrl = "";
    this.isGifPlaying = true;
  }
  setPositions(data: any) {
    this.pendingCropParams = this.toImagePreviewModel(data);
  }
  onCropUpdate(data: ImagePreviewModel) {
    this.setPositions(data);     
  }
  onLoadFile(file: File) {
    this.cropFile = file;
    var fileToLoad = file;
    var fileReader = new FileReader();

    fileReader.onload = async (fileLoadedEvent: any) => {
      var srcData = fileLoadedEvent.target.result;
      this.updateCropLimits();
      this.pendingCropParams = null;
      this.isCropGifPlaying = true;
      this.cropGifStillImageUrl = this.isGifImage(srcData)
        ? await this.createStillImage(srcData)
        : "";
      this.cropImageUrl = srcData;
    };
    fileReader.readAsDataURL(fileToLoad);
  }
  async saveCrop() {
    this.file = this.cropFile;
    this.cropFile = null;
    const cropParams = this.getCurrentCropParams();
    const normalizedImage = await this.normalizeImage(this.cropImageUrl, cropParams);
    this.innervalue = new ImagePreviewModel({
      ImageUrl: normalizedImage.dataUrl,
      Width: normalizedImage.width,
      Height: normalizedImage.height,
      Top: normalizedImage.top,
      Left: normalizedImage.left,
    });
    this.isGifPlaying = true;
    this.gifStillImageUrl = this.isGifImage(normalizedImage.dataUrl)
      ? await this.createStillImage(normalizedImage.dataUrl)
      : "";
    this.cropImageUrl = null;
    this.cropGifStillImageUrl = "";
    this.isCropGifPlaying = true;
    this.clearFile();
    this.isValid();  
  }

  normalizeImage(source: string, cropParams: ImagePreviewModel = null): Promise<{ dataUrl: string; width: number; height: number; top: number; left: number }> {
    return new Promise((resolve) => {
      const image = new Image();
      image.onload = async () => {
        const sourceWidth = image.naturalWidth || image.width || 1;
        const sourceHeight = image.naturalHeight || image.height || 1;
        const targetAspectRatio = this.aspectRatio || 1;

        let cropWidth = Math.round(this.getFiniteNumber(cropParams && cropParams.Width, sourceWidth));
        let cropHeight = Math.round(this.getFiniteNumber(cropParams && cropParams.Height, sourceHeight));
        let cropLeft = Math.round(this.getFiniteNumber(cropParams && cropParams.Left, 0));
        let cropTop = Math.round(this.getFiniteNumber(cropParams && cropParams.Top, 0));
        const sourceAspectRatio = sourceWidth / sourceHeight;

        if (!cropParams && targetAspectRatio > 0) {
          if (sourceAspectRatio > targetAspectRatio) {
            cropWidth = Math.round(sourceHeight * targetAspectRatio);
            cropLeft = Math.max(0, Math.round((sourceWidth - cropWidth) / 2));
          } else if (sourceAspectRatio < targetAspectRatio) {
            cropHeight = Math.round(sourceWidth / targetAspectRatio);
            cropTop = Math.max(0, Math.round((sourceHeight - cropHeight) / 2));
          }
        }

        cropLeft = Math.max(0, Math.min(cropLeft, sourceWidth - 1));
        cropTop = Math.max(0, Math.min(cropTop, sourceHeight - 1));
        cropWidth = Math.max(1, Math.min(cropWidth, sourceWidth - cropLeft));
        cropHeight = Math.max(1, Math.min(cropHeight, sourceHeight - cropTop));

        if (this.shouldPreserveOriginalGif(source)) {
          const croppedGif = await this.cropAnimatedGif(source, cropLeft, cropTop, cropWidth, cropHeight);
          resolve({
            dataUrl: croppedGif || source,
            width: cropWidth,
            height: cropHeight,
            top: 0,
            left: 0,
          });
          return;
        }

        const targetWidth = cropWidth;
        const targetHeight = cropHeight;
        const canvas = document.createElement("canvas");
        canvas.width = targetWidth;
        canvas.height = targetHeight;
        const context = canvas.getContext("2d");

        if (!context) {
          resolve({
            dataUrl: source,
            width: image.naturalWidth || 0,
            height: image.naturalHeight || 0,
            top: 0,
            left: 0,
          });
          return;
        }

        const outputMimeType = this.getOutputMimeType(source);

        try {
          if (outputMimeType !== "image/png") {
            context.fillStyle = "#ffffff";
            context.fillRect(0, 0, targetWidth, targetHeight);
          }
          context.drawImage(
            image,
            cropLeft,
            cropTop,
            cropWidth,
            cropHeight,
            0,
            0,
            targetWidth,
            targetHeight
          );
        } catch (error) {
          resolve({
            dataUrl: source,
            width: image.naturalWidth || image.width || 0,
            height: image.naturalHeight || image.height || 0,
            top: 0,
            left: 0,
          });
          return;
        }

        const dataUrl = canvas.toDataURL(outputMimeType, 0.92);

        resolve({
          dataUrl: dataUrl && dataUrl !== "data:," ? dataUrl : source,
          width: targetWidth,
          height: targetHeight,
          top: 0,
          left: 0,
        });
      };
      image.onerror = () => {
        resolve({
          dataUrl: source,
          width: this.innervalue.Width || 0,
          height: this.innervalue.Height || 0,
          top: this.innervalue.Top || 0,
          left: this.innervalue.Left || 0,
        });
      };
      image.src = source;
    });
  }

  shouldPreserveOriginalGif(source: string): boolean {
    return this.preserveGif && this.isGifImage(source);
  }

  isGifImage(source: string): boolean {
    return /^data:image\/gif(?:;|,)/i.test(source || "");
  }

  async cropAnimatedGif(source: string, cropLeft: number, cropTop: number, cropWidth: number, cropHeight: number): Promise<string> {
    try {
      const buffer = this.dataUrlToArrayBuffer(source);
      const parsedGif = parseGIF(buffer);
      const frames = decompressFrames(parsedGif, true);
      if (!frames || !frames.length) {
        return "";
      }

      const sourceCanvas = document.createElement("canvas");
      const sourceContext = sourceCanvas.getContext("2d");
      const frameCanvas = document.createElement("canvas");
      const frameContext = frameCanvas.getContext("2d");
      if (!sourceContext || !frameContext) {
        return "";
      }

      sourceCanvas.width = parsedGif.lsd.width;
      sourceCanvas.height = parsedGif.lsd.height;
      frameCanvas.width = cropWidth;
      frameCanvas.height = cropHeight;

      const encoder = GIFEncoder();
      frames.forEach((frame: any, index: number) => {
        const previousFrame = sourceContext.getImageData(0, 0, sourceCanvas.width, sourceCanvas.height);
        const patchImageData = new ImageData(frame.patch, frame.dims.width, frame.dims.height);
        sourceContext.putImageData(patchImageData, frame.dims.left, frame.dims.top);

        frameContext.clearRect(0, 0, cropWidth, cropHeight);
        frameContext.drawImage(
          sourceCanvas,
          cropLeft,
          cropTop,
          cropWidth,
          cropHeight,
          0,
          0,
          cropWidth,
          cropHeight
        );

        const croppedFrame = frameContext.getImageData(0, 0, cropWidth, cropHeight);
        const palette = quantize(croppedFrame.data, 256, { format: "rgb565" });
        const indexedFrame = applyPalette(croppedFrame.data, palette, "rgb565");
        encoder.writeFrame(indexedFrame, cropWidth, cropHeight, {
          palette,
          delay: frame.delay || 100,
          repeat: 0,
          first: index === 0,
          dispose: 1,
        });

        if (frame.disposalType === 2) {
          sourceContext.clearRect(frame.dims.left, frame.dims.top, frame.dims.width, frame.dims.height);
        } else if (frame.disposalType === 3) {
          sourceContext.putImageData(previousFrame, 0, 0);
        }
      });
      encoder.finish();
      return this.bytesToDataUrl(encoder.bytes(), "image/gif");
    } catch (error) {
      console.error("Failed to crop animated gif", error);
      return "";
    }
  }

  dataUrlToArrayBuffer(dataUrl: string): ArrayBuffer {
    const base64 = (dataUrl.split(",")[1] || "");
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes.buffer;
  }

  bytesToDataUrl(bytes: Uint8Array, mimeType: string): string {
    let binary = "";
    const chunkSize = 0x8000;
    for (let i = 0; i < bytes.length; i += chunkSize) {
      const chunk = bytes.subarray(i, i + chunkSize);
      binary += String.fromCharCode.apply(null, Array.prototype.slice.call(chunk));
    }
    return `data:${mimeType};base64,${btoa(binary)}`;
  }

  async createStillImage(source: string): Promise<string> {
    return new Promise((resolve) => {
      const image = new Image();
      image.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = image.naturalWidth || image.width || 1;
        canvas.height = image.naturalHeight || image.height || 1;
        const context = canvas.getContext("2d");
        if (!context) {
          resolve("");
          return;
        }
        context.drawImage(image, 0, 0);
        resolve(canvas.toDataURL("image/png"));
      };
      image.onerror = () => resolve("");
      image.src = source;
    });
  }

  async toggleGifPlayback() {
    if (!this.gifStillImageUrl && this.innervalue && this.innervalue.ImageUrl) {
      this.gifStillImageUrl = await this.createStillImage(this.innervalue.ImageUrl);
    }
    this.isGifPlaying = !this.isGifPlaying;
  }

  async toggleCropGifPlayback() {
    this.pendingCropParams = this.getCurrentCropParams();
    if (!this.cropGifStillImageUrl && this.cropImageUrl) {
      this.cropGifStillImageUrl = await this.createStillImage(this.cropImageUrl);
    }
    this.isCropGifPlaying = !this.isCropGifPlaying;
  }

  getOutputMimeType(source: string): string {
    if (/^data:image\/png(?:;|,)/i.test(source || "")) {
      return "image/png";
    }
    return "image/jpeg";
  }

  getFiniteNumber(value: any, fallback: number): number {
    const parsed = Number(value);
    return isFinite(parsed) && parsed > 0 ? parsed : fallback;
  }

  getPreviewNaturalHeight(): number {
    const height = this.innervalue && this.innervalue.Height;
    const width = this.innervalue && this.innervalue.Width;
    const aspectRatio = this.aspectRatio || 1;
    return height && width ? Math.max(height, width / aspectRatio) : 182;
  }

  getCurrentCropParams(): ImagePreviewModel {
    const crop = this.$refs.crop as any;
    const cropParams = crop && typeof crop.getCropParams === "function"
      ? crop.getCropParams()
      : this.pendingCropParams;
    return this.toImagePreviewModel(cropParams);
  }

  toImagePreviewModel(data: any): ImagePreviewModel {
    if (!data) {
      return null;
    }
    return new ImagePreviewModel({
      ImageUrl: this.cropImageUrl || (this.innervalue && this.innervalue.ImageUrl) || "",
      Width: Number(data.Width ?? data.width) || 0,
      Height: Number(data.Height ?? data.height) || 0,
      Top: Number(data.Top ?? data.top) || 0,
      Left: Number(data.Left ?? data.left) || 0
    });
  }

  syncExistingImageMeta() {
    if (!this.innervalue || !this.innervalue.ImageUrl) {
      return;
    }
    if (this.innervalue.Width && this.innervalue.Height) {
      return;
    }

    const image = new Image();
    image.onload = () => {
      if (!this.innervalue || !this.innervalue.ImageUrl) {
        return;
      }
      this.innervalue = new ImagePreviewModel({
        ImageUrl: this.innervalue.ImageUrl,
        Width: image.naturalWidth || 0,
        Height: image.naturalHeight || 0,
        Top: 0,
        Left: 0
      });
    };
    image.src = this.innervalue.ImageUrl;
  }

  updateCropLimits() {
    const viewportHeight = window.innerHeight || 800;
    const viewportWidth = window.innerWidth || 1024;
    this.cropMaxHeight = Math.max(180, Math.min(300, viewportHeight - 360));
    this.cropMaxWidth = Math.max(260, Math.min(450, viewportWidth - 180));
  }

  getSetParams()
  {
    return this.innervalue;
  }

  isValid(): boolean {
    this.errorMessage = "";
    if (this.required && (!this.innervalue.ImageUrl || this.innervalue.ImageUrl == '/assets/img/no_image.svg')) {
      this.errorMessage = "*Добавте изображение";
      this.hasError = true;
      return false;
    }
    this.hasError = false;
    return true;
  }
  

  

  getFile() {
    return this.innervalue;
  }

  clearFile() {
    this.cropImageUrl = null;
    (this.$refs.fileInput as UiFileInputComponent).setFile(this.file);
  }
}
